import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, Star, Truck, MapPin, Wallet, ArrowLeft, Crown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PODIUM_STYLES = {
  1: {
    badge: "bg-gradient-to-br from-yellow-400 to-amber-500",
    ring: "ring-yellow-400/50",
    label: "Or",
    Icon: Crown,
  },
  2: {
    badge: "bg-gradient-to-br from-slate-300 to-slate-400",
    ring: "ring-slate-300/50",
    label: "Argent",
    Icon: Medal,
  },
  3: {
    badge: "bg-gradient-to-br from-orange-400 to-orange-600",
    ring: "ring-orange-400/50",
    label: "Bronze",
    Icon: Medal,
  },
};

const formatNumber = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function Leaderboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      let boardResponse;
      if (period === "top-rated") {
        boardResponse = await axios.get(`${API}/leaderboard/top-rated?limit=10&min_deliveries=5`);
      } else {
        boardResponse = await axios.get(`${API}/leaderboard/couriers?period=${period}&limit=20`);
      }
      const mine = user?.id && user?.role === "courier" && period !== "top-rated"
        ? await axios.get(`${API}/leaderboard/me/${user.id}?period=${period}`)
        : { data: null };
      setData(boardResponse.data);
      setMyRank(mine.data);
    } catch (err) {
      console.error("Erreur leaderboard:", err);
    } finally {
      setLoading(false);
    }
  }, [period, user?.id, user?.role]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const leaderboard = data?.leaderboard || [];
  const podium = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  const monthlyBonus = data?.monthly_bonus || { 1: 10000, 2: 5000, 3: 3000 };

  const isCourier = user?.role === "courier";

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="leaderboard-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#003594] via-[#0058D4] to-[#D4AF37] px-4 md:px-8 pt-6 pb-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, white 0%, transparent 40%), radial-gradient(circle at 80% 30%, white 0%, transparent 40%)",
        }} />
        <div className="relative max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button
              onClick={() => navigate(isCourier ? "/courier" : -1)}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="leaderboard-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Trophy className="w-7 h-7" />
            <h1
              className="text-3xl md:text-4xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="leaderboard-title"
            >
              Top Livreurs ARTEKNOS
            </h1>
          </div>
          <p className="text-white/80 max-w-2xl text-sm md:text-base">
            Le classement officiel des meilleurs livreurs. Les 3 premiers du mois reçoivent une prime
            automatique en crédits ARTEKNOS — créditée le 1er du mois suivant.
          </p>

          {/* Period switch */}
          <div className="mt-6 inline-flex bg-black/30 backdrop-blur rounded-full p-1 border border-white/20">
            {[
              { key: "week", label: "Cette semaine" },
              { key: "month", label: "Ce mois" },
              { key: "top-rated", label: "⭐ Top notés" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setPeriod(opt.key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  period === opt.key
                    ? "bg-white text-[#003594] shadow"
                    : "text-white hover:bg-white/10"
                }`}
                data-testid={`period-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bonus banner */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 -mt-5 relative z-10">
        <Card className="bg-gradient-to-r from-[#1A1A1A] to-[#2A1A0F] border-yellow-500/40 border">
          <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Crown className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="font-bold text-white">Primes mensuelles automatiques</p>
                <p className="text-xs text-yellow-300/80">
                  Top 1 : {formatNumber(monthlyBonus[1])} FCFA · Top 2 : {formatNumber(monthlyBonus[2])} FCFA · Top 3 : {formatNumber(monthlyBonus[3])} FCFA
                </p>
              </div>
            </div>
            <span className="text-xs text-white/60">
              {period === "top-rated"
                ? "Classement pur note client · min. 5 livraisons sur 30 jours"
                : "Score = (note × 200) + (livraisons × 100) + (km × 5)"}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* My rank (couriers only) */}
      {isCourier && myRank && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mt-4">
          <Card className="bg-[#0F1B33] border-[#003594]/40 border" data-testid="my-rank-card">
            <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#003594] flex items-center justify-center font-black text-lg" data-testid="my-rank-position">
                  {myRank.rank ? `#${myRank.rank}` : "—"}
                </div>
                <div>
                  <p className="text-xs text-white/60">Votre position</p>
                  <p className="font-bold text-white" data-testid="my-rank-name">
                    {myRank.stats?.full_name || "Vous"}
                  </p>
                  <p className="text-xs text-white/70">
                    {myRank.stats?.deliveries || 0} livraisons · {myRank.stats?.total_km || 0} km · note {(myRank.stats?.rating || 0).toFixed(1)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-[#D4AF37]" data-testid="my-rank-score">
                  {formatNumber(myRank.stats?.score || 0)}
                </p>
                <p className="text-xs text-white/60">points</p>
                {myRank.next_rank_gap !== null && myRank.next_rank_gap !== undefined && (
                  <p className="text-xs text-yellow-400 mt-1 flex items-center justify-end gap-1">
                    <TrendingUp className="w-3 h-3" />
                    +{formatNumber(myRank.next_rank_gap)} pour le rang suivant
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading / Empty */}
      {loading && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mt-10 text-center text-white/60">
          Chargement du classement…
        </div>
      )}

      {!loading && leaderboard.length === 0 && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mt-10 text-center" data-testid="leaderboard-empty">
          <div className="bg-[#1A1A1A] rounded-2xl py-12 px-6 border border-white/10">
            <Trophy className="w-12 h-12 text-white/30 mx-auto mb-3" />
            <p className="text-white/70">Aucune livraison sur cette période. Soyez le premier !</p>
          </div>
        </div>
      )}

      {/* Podium */}
      {!loading && podium.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mt-8">
          <div className="grid grid-cols-3 gap-3 md:gap-6 items-end" data-testid="podium">
            {[2, 1, 3].map((rank) => {
              const entry = podium.find((p) => p.rank === rank);
              if (!entry) return <div key={rank} />;
              const style = PODIUM_STYLES[rank];
              const Icon = style.Icon;
              const isFirst = rank === 1;
              return (
                <div
                  key={entry.courier_id}
                  className={`flex flex-col items-center ${isFirst ? "order-2" : rank === 2 ? "order-1" : "order-3"}`}
                  data-testid={`podium-${rank}`}
                >
                  <div className={`relative ${isFirst ? "scale-110" : ""}`}>
                    <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${style.badge} flex items-center justify-center ring-4 ${style.ring} shadow-2xl`}>
                      <Truck className="w-9 h-9 md:w-10 md:h-10 text-white" />
                    </div>
                    <div className="absolute -top-3 -right-1 bg-[#0F172A] rounded-full p-1.5 border-2 border-current" style={{ color: rank === 1 ? "#FACC15" : rank === 2 ? "#CBD5E1" : "#FB923C" }}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="font-bold text-white mt-3 text-center text-sm md:text-base truncate max-w-full px-1">
                    {entry.full_name}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-white/70 mt-0.5">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span>{entry.rating.toFixed(1)}</span>
                  </div>
                  <div className={`mt-3 px-3 py-1.5 rounded-full text-xs font-bold ${style.badge} text-white shadow`}>
                    {formatNumber(entry.score)} pts
                  </div>
                  <p className="text-[11px] text-white/50 mt-1">
                    {entry.deliveries} liv. · {entry.total_km} km
                  </p>
                  <p className="text-[11px] text-yellow-400 mt-1 font-semibold">
                    🏆 {formatNumber(monthlyBonus[rank])} FCFA / mois
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rest of the table */}
      {!loading && rest.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mt-8 mb-12">
          <h2 className="text-base font-semibold text-white/80 mb-3">Autres livreurs classés</h2>
          <div className="bg-[#1A1A1A] rounded-2xl overflow-hidden border border-white/5">
            {rest.map((entry) => (
              <div
                key={entry.courier_id}
                className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                data-testid={`leaderboard-row-${entry.rank}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#2D2D2D] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {entry.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{entry.full_name}</p>
                  <div className="flex items-center gap-3 text-xs text-white/60 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      {entry.rating.toFixed(1)}
                    </span>
                    <span>{entry.deliveries} livraisons</span>
                    <span className="hidden md:inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {entry.total_km} km
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#D4AF37] font-bold">{formatNumber(entry.score)}</p>
                  <p className="text-[10px] text-white/40">points</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign-out */}
      {onLogout && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 pb-10 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-white/40 hover:text-white"
          >
            Déconnexion
          </Button>
        </div>
      )}
    </div>
  );
}
